import { api, ApiError, type Asset, type Colour, type FastextSpec, type Section } from '../api.ts';
import { Preview } from '../preview.ts';
import { COLOURS, confirmDialog, debounce, el, field, select, toast } from '../ui.ts';

const BLANK: Section = {
  key: '', title: '', indexPage: 150, firstPage: 151, lastPage: 169, categories: [], band: 'red', titleColour: 'white', bannerAsset: null, bannerStyle: 'band',
  fastext: { red: ['Main', 100], green: ['News', 101], yellow: ['Weather', 400], blue: ['TV', 600] }, showOnIndex: true, indexLabel: null, sort: 0, enabled: true,
};

export async function sectionsList(): Promise<HTMLElement> {
  const [sections, assets] = await Promise.all([api.sections(), api.assets()]);
  const assetName = (id: string | null) => assets.find((a) => a.id === id)?.name ?? null;
  return el('section', {},
    el('div', { class: 'head' }, el('h1', {}, 'Sections'), el('a', { class: 'button primary', href: '#/sections/new' }, 'New section')),
    el('p', { class: 'muted' }, 'A section owns an index page and a range of story pages, filled by the categories it takes. Its banner is either a coloured band with the title, or block art.'),
    el('table', {}, el('thead', {}, el('tr', {}, ['Section', 'Index', 'Stories', 'Categories', 'Banner', 'On index', 'Enabled'].map((h) => el('th', {}, h)))),
      el('tbody', {}, sections.map((s) => el('tr', {},
        el('td', {}, el('a', { href: `#/sections/${s.key}` }, s.title), el('small', { class: 'muted' }, ` ${s.key}`)),
        el('td', {}, String(s.indexPage)),
        el('td', {}, `${s.firstPage}–${s.lastPage}`),
        el('td', {}, s.categories.join(', ')),
        el('td', {}, s.bannerAsset ? `art: ${assetName(s.bannerAsset) ?? s.bannerAsset}` : `${s.bannerStyle} (${s.band})`),
        el('td', {}, s.showOnIndex ? (s.indexLabel ?? s.title) : '—'),
        el('td', {}, s.enabled ? 'yes' : 'no'),
      )))),
  );
}

function fastextFields(f: FastextSpec, onInput: () => void): { root: HTMLElement; value: () => FastextSpec } {
  const keys = ['red', 'green', 'yellow', 'blue'] as const;
  const inputs = keys.map((k) => ({
    label: el('input', { type: 'text', value: f[k][0], maxlength: '9', onInput }),
    page: el('input', { type: 'number', value: String(f[k][1]), min: '100', max: '899', onInput }),
  }));
  const root = el('div', { class: 'fastext-grid' }, keys.map((k, i) => el('div', { class: `ft ${k}` }, el('span', {}, k), inputs[i]!.label, inputs[i]!.page)));
  return { root, value: () => ({ red: [inputs[0]!.label.value, Number(inputs[0]!.page.value)], green: [inputs[1]!.label.value, Number(inputs[1]!.page.value)], yellow: [inputs[2]!.label.value, Number(inputs[2]!.page.value)], blue: [inputs[3]!.label.value, Number(inputs[3]!.page.value)] }) };
}

export async function sectionEditor(key: string | null): Promise<HTMLElement> {
  const [sections, assets] = await Promise.all([api.sections(), api.assets()]);
  const existing = key ? sections.find((s) => s.key === key) ?? null : null;
  const s: Section = existing ? { ...existing } : { ...BLANK, sort: sections.length };
  const isNew = !existing;

  const keyInput = el('input', { type: 'text', value: s.key, placeholder: 'e.g. local', pattern: '[a-z0-9-]{2,24}', disabled: !isNew });
  const title = el('input', { type: 'text', value: s.title, placeholder: 'Banner title, e.g. LOCAL NEWS', maxlength: '36' });
  const indexPage = el('input', { type: 'number', value: String(s.indexPage), min: '101', max: '899' });
  const firstPage = el('input', { type: 'number', value: String(s.firstPage), min: '101', max: '899' });
  const lastPage = el('input', { type: 'number', value: String(s.lastPage), min: '101', max: '899' });
  const categories = el('input', { type: 'text', value: s.categories.join(', '), placeholder: 'news, uk, politics' });
  const band = select(COLOURS.map((c) => ({ value: c, label: c })), s.band);
  const titleColour = select(COLOURS.map((c) => ({ value: c, label: c })), s.titleColour);
  const artAssets = assets.filter((a) => a.height <= 6);
  const bannerKind = select([
    { value: 'band', label: 'Coloured band with the title (title from column 4)' },
    { value: 'bar', label: 'Bar above, title in the band colour on black (title from column 2)' },
    { value: 'art', label: 'Block art asset' },
  ], s.bannerAsset ? 'art' : s.bannerStyle);
  const bannerAsset = select([{ value: '', label: '— choose an asset —' }, ...artAssets.map((a: Asset) => ({ value: a.id, label: `${a.name} (${a.width}×${a.height})` }))], s.bannerAsset ?? '');
  const showOnIndex = el('input', { type: 'checkbox', checked: s.showOnIndex });
  const indexLabel = el('input', { type: 'text', value: s.indexLabel ?? '', placeholder: 'defaults to the title', maxlength: '14' });
  const sort = el('input', { type: 'number', value: String(s.sort) });
  const enabled = el('input', { type: 'checkbox', checked: s.enabled });
  const preview = new Preview(9);

  const value = (): Section => ({
    key: isNew ? keyInput.value.trim() : s.key,
    title: title.value.trim(),
    indexPage: Number(indexPage.value), firstPage: Number(firstPage.value), lastPage: Number(lastPage.value),
    categories: categories.value.split(',').map((c) => c.trim().toLowerCase()).filter(Boolean),
    band: band.value as Colour, titleColour: titleColour.value as Colour,
    bannerAsset: bannerKind.value === 'art' && bannerAsset.value ? bannerAsset.value : null,
    bannerStyle: bannerKind.value === 'bar' ? 'bar' : 'band',
    fastext: ft.value(),
    showOnIndex: showOnIndex.checked, indexLabel: indexLabel.value.trim() || null, sort: Number(sort.value), enabled: enabled.checked,
  });
  const refresh = debounce(async () => {
    try { preview.show(await api.preview({ kind: 'section', section: value() })); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'preview failed', 'error'); }
  }, 350);
  const ft = fastextFields(s.fastext, refresh);
  const bannerAssetField = field('Art asset', bannerAsset, 'Assets up to 6 rows tall; make one under Assets → New block art (40×4 is the banner preset).');
  const syncBanner = () => { bannerAssetField.hidden = bannerKind.value !== 'art'; refresh(); };
  for (const i of [title, indexPage, firstPage, lastPage, categories, band, titleColour, bannerAsset, showOnIndex, indexLabel, enabled]) i.addEventListener('input', refresh);
  bannerKind.addEventListener('input', syncBanner);
  syncBanner();

  const actions = el('div', { class: 'actions' },
    el('button', { type: 'button', class: 'primary', onClick: async () => {
      try {
        const saved = await api.saveSection(value());
        toast(`Saved — pages ${saved.indexPage} and ${saved.firstPage}–${saved.lastPage} re-allocate now`);
        if (isNew) location.hash = `#/sections/${saved.key}`;
      } catch (e) { toast(e instanceof ApiError ? e.message : 'save failed', 'error'); }
    } }, 'Save'),
    !isNew && el('button', { type: 'button', class: 'danger', onClick: async () => {
      if (!confirmDialog(`Delete section "${s.title}" and take its pages out of service?`)) return;
      try { await api.deleteSection(s.key); location.hash = '#/sections'; } catch (e) { toast(e instanceof ApiError ? e.message : 'failed', 'error'); }
    } }, 'Delete'),
  );

  return el('section', { class: 'editor' },
    el('div', { class: 'head' }, el('h1', {}, isNew ? 'New section' : `Section: ${s.title}`)),
    el('div', { class: 'cols' },
      el('div', { class: 'form' },
        el('div', { class: 'frow' }, field('Key', keyInput, isNew ? 'Short id, letters/digits/dashes; fixed once saved.' : 'Fixed.'), field('Title', title, 'Shown in the banner (unless art) and used as the index label.')),
        el('div', { class: 'frow' }, field('Index page', indexPage), field('First story page', firstPage), field('Last story page', lastPage)),
        field('Categories', categories, 'Content items with these categories land here (feeds and articles). Comma separated.'),
        el('div', { class: 'frow' }, field('Band colour', band), field('Title colour', titleColour)),
        field('Banner', bannerKind),
        bannerAssetField,
        field('Fastext (label and page)', ft.root),
        el('div', { class: 'frow' }, field('Show on main index', showOnIndex), field('Index label', indexLabel), field('Order', sort), field('Enabled', enabled)),
        actions,
      ),
      preview.root,
    ),
  );
}
